// Editing values: popovers for picking (choices, people, linked records, long text) and the full-width inputs
// used on the record panel and in forms.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Field, Links, Person, PersonRow, Value } from "./pApi";
import { Avatar, Pill } from "./Cell";

// ---------- popover ----------
export function Popover({ anchor, onClose, children, width = 280, className = "" }: { anchor: DOMRect | HTMLElement | null; onClose: () => void; children: ReactNode; width?: number; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const r = anchor instanceof HTMLElement ? anchor.getBoundingClientRect() : anchor;
    if (!r) return;
    const h = box.current?.offsetHeight ?? 300;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = r.bottom + 4;
    const top = below + h > window.innerHeight - 8 && r.top - h - 4 > 8 ? r.top - h - 4 : Math.min(below, Math.max(8, window.innerHeight - h - 8));
    setPos({ left, top });
  }, [anchor, width]);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    const t = setTimeout(() => document.addEventListener("mousedown", down), 0);
    document.addEventListener("keydown", key, true);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key, true); };
  }, [onClose]);
  return createPortal(
    <div ref={box} className={`pv-pop ${className}`} style={{ width, left: pos?.left ?? -9999, top: pos?.top ?? -9999 }} role="dialog">{children}</div>,
    document.body,
  );
}

/** A searchable list with keyboard navigation. */
function PickList<T>({ items, label, render, onPick, selected, placeholder, footer, empty }: {
  items: T[]; label: (t: T) => string; render?: (t: T) => ReactNode; onPick: (t: T) => void; selected?: (t: T) => boolean; placeholder: string; footer?: (q: string) => ReactNode; empty?: string;
}) {
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const shown = useMemo(() => items.filter((t) => label(t).toLowerCase().includes(q.trim().toLowerCase())).slice(0, 200), [items, q, label]);
  useEffect(() => setAt(0), [q]);
  return (
    <div className="pv-pick">
      <input autoFocus className="input" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setAt((a) => Math.min(a + 1, shown.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setAt((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && shown[at]) { e.preventDefault(); onPick(shown[at]); }
        }} />
      <ul className="pv-pick-list" role="listbox">
        {shown.map((t, i) => (
          <li key={i} role="option" aria-selected={selected?.(t) ?? false} className={i === at ? "at" : ""} onMouseEnter={() => setAt(i)} onMouseDown={(e) => { e.preventDefault(); onPick(t); }}>
            <span className="pv-tick">{selected?.(t) ? "✓" : ""}</span>{render ? render(t) : label(t)}
          </li>
        ))}
        {!shown.length && <li className="pv-pick-empty">{empty ?? "Nothing matches"}</li>}
      </ul>
      {footer?.(q)}
    </div>
  );
}

export function ChoiceEditor({ f, value, anchor, onChange, onClose, onAddChoice }: { f: Field; value: Value | undefined; anchor: DOMRect | HTMLElement; onChange: (v: unknown) => void; onClose: () => void; onAddChoice?: (name: string) => Promise<void> }) {
  const multi = f.type === "multiselect";
  const cur = multi ? ((value as string[] | null) ?? []) : value ? [String(value)] : [];
  const [sel, setSel] = useState<string[]>(cur);
  const choices = f.options.choices ?? [];
  const pick = (name: string) => {
    if (!multi) { onChange(sel[0] === name ? null : name); onClose(); return; }
    const next = sel.includes(name) ? sel.filter((x) => x !== name) : [...sel, name];
    setSel(next); onChange(next);
  };
  return (
    <Popover anchor={anchor} onClose={onClose}>
      <PickList items={choices.map((c) => c.name)} label={(x) => x} render={(x) => <Pill f={f} name={x} />} selected={(x) => sel.includes(x)} onPick={pick} placeholder="Find a choice" empty="No choices yet"
        footer={(q) => onAddChoice && q.trim() && !choices.some((c) => c.name.toLowerCase() === q.trim().toLowerCase()) ? (
          <button className="pv-pick-add" onMouseDown={async (e) => { e.preventDefault(); await onAddChoice(q.trim()); pick(q.trim()); }}>+ Add “{q.trim()}” as a choice</button>
        ) : null} />
      {!multi && sel.length > 0 && <button className="pv-pick-clear" onMouseDown={(e) => { e.preventDefault(); onChange(null); onClose(); }}>Clear</button>}
    </Popover>
  );
}

export function PersonEditor({ value, anchor, people, onChange, onClose }: { value: Value | undefined; anchor: DOMRect | HTMLElement; people: PersonRow[]; onChange: (v: unknown) => void; onClose: () => void }) {
  const cur = value && typeof value === "object" ? (value as Person).id : null;
  return (
    <Popover anchor={anchor} onClose={onClose}>
      <PickList items={people} label={(p) => `${p.name} ${p.email ?? ""}`} render={(p) => <span className="pv-person"><Avatar name={p.name} />{p.name}<span className="muted" style={{ fontSize: 12 }}>{p.email}</span></span>}
        selected={(p) => p.id === cur} onPick={(p) => { onChange(p.id === cur ? null : p.id); onClose(); }} placeholder="Find a person" empty="Only people who have signed in to courageMFG can be picked" />
      {cur && <button className="pv-pick-clear" onMouseDown={(e) => { e.preventDefault(); onChange(null); onClose(); }}>Clear</button>}
    </Popover>
  );
}

export function LinkEditor({ f, value, anchor, links, onChange, onClose, selfId }: { f: Field; value: Value | undefined; anchor: DOMRect | HTMLElement; links: Links; onChange: (v: unknown) => void; onClose: () => void; selfId?: string }) {
  const t = f.options.table ? links[f.options.table] : undefined;
  const [sel, setSel] = useState<string[]>(((value as string[] | null) ?? []));
  const rows = (t?.rows ?? []).filter((r) => r.id !== selfId);
  const pick = (id: string) => {
    const next = f.options.single ? (sel.includes(id) ? [] : [id]) : sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    setSel(next); onChange(next);
    if (f.options.single) onClose();
  };
  return (
    <Popover anchor={anchor} onClose={onClose} width={320}>
      <div className="pv-pop-h">{t ? `Records in ${t.name}` : "Linked table"}</div>
      {t?.restricted ? <div className="pv-pick-empty">You can't open {t.name}.</div> :
        <PickList items={rows} label={(r) => r.label} selected={(r) => sel.includes(r.id)} onPick={(r) => pick(r.id)} placeholder="Find a record" empty={rows.length ? "Nothing matches" : `${t?.name ?? "That table"} has no records yet`} />}
    </Popover>
  );
}

export function LongTextEditor({ value, anchor, onChange, onClose }: { value: Value | undefined; anchor: DOMRect | HTMLElement; onChange: (v: unknown) => void; onClose: () => void }) {
  const [v, setV] = useState(String(value ?? ""));
  const done = () => { if (v !== String(value ?? "")) onChange(v); onClose(); };
  return (
    <Popover anchor={anchor} onClose={done} width={380}>
      <textarea autoFocus className="textarea pv-long" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); done(); } }} />
      <div className="pv-pop-f"><span className="muted">Ctrl+Enter to save</span><button className="btn primary" onClick={done}>Done</button></div>
    </Popover>
  );
}

// ---------- full-width inputs (record panel, forms) ----------
/** An input for one field. Text-like inputs save on blur; pickers save as they change. */
export function FieldInput({ f, value, onChange, links, people, disabled, id, selfId, compactLinks }: {
  f: Field; value: Value | undefined; onChange: (v: unknown) => void; links?: Links; people?: PersonRow[]; disabled?: boolean; id?: string; selfId?: string; compactLinks?: boolean;
}) {
  const [draft, setDraft] = useState<string>(() => (value === null || value === undefined ? "" : typeof value === "object" ? "" : String(value)));
  const [open, setOpen] = useState<HTMLElement | null>(null);
  useEffect(() => { setDraft(value === null || value === undefined ? "" : typeof value === "object" ? "" : String(value)); }, [value]);
  const commit = () => { const cur = value === null || value === undefined ? "" : String(value); if (draft !== cur) onChange(draft === "" ? null : draft); };
  switch (f.type) {
    case "longtext": return <textarea id={id} className="textarea" rows={4} disabled={disabled} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} />;
    case "checkbox": return <label className="pv-cb"><input id={id} type="checkbox" disabled={disabled} checked={!!value} onChange={(e) => onChange(e.target.checked)} /> Yes</label>;
    case "select": return (
      <select id={id} className="select" disabled={disabled} value={value ? String(value) : ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">—</option>{(f.options.choices ?? []).map((c) => <option key={c.name}>{c.name}</option>)}
      </select>
    );
    case "multiselect": {
      const cur = (value as string[] | null) ?? [];
      return (
        <div className="pv-multi">
          {(f.options.choices ?? []).map((c) => (
            <label key={c.name} className={`pv-multi-o ${cur.includes(c.name) ? "on" : ""}`}>
              <input type="checkbox" disabled={disabled} checked={cur.includes(c.name)} onChange={() => onChange(cur.includes(c.name) ? cur.filter((x) => x !== c.name) : [...cur, c.name])} /><Pill f={f} name={c.name} />
            </label>
          ))}
        </div>
      );
    }
    case "person": return (
      <select id={id} className="select" disabled={disabled} value={value && typeof value === "object" ? (value as Person).id : ""} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">—</option>{(people ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    );
    case "link": {
      const ids = (value as string[] | null) ?? [];
      const t = f.options.table ? links?.[f.options.table] : undefined;
      return (
        <div className="pv-linkf">
          {ids.map((x) => (
            <span key={x} className="pv-chip">{t?.rows.find((r) => r.id === x)?.label ?? "…"}{!disabled && <button aria-label="Remove" onClick={() => onChange(ids.filter((y) => y !== x))}>×</button>}</span>
          ))}
          {!disabled && <button className="pv-chip add" onClick={(e) => setOpen(e.currentTarget)}>{compactLinks ? "+" : `+ Link a record${t ? ` from ${t.name}` : ""}`}</button>}
          {open && links && <LinkEditor f={f} value={ids} anchor={open} links={links} selfId={selfId} onChange={onChange} onClose={() => setOpen(null)} />}
        </div>
      );
    }
    case "date": return <input id={id} className="input" type="date" disabled={disabled} value={draft} onChange={(e) => { setDraft(e.target.value); onChange(e.target.value || null); }} />;
    case "number": case "currency": case "percent":
      return <div className="pv-numin">{f.type === "currency" && <span>$</span>}<input id={id} className="input" inputMode="decimal" disabled={disabled} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />{f.type === "percent" && <span>%</span>}</div>;
    default:
      return <input id={id} className="input" type={f.type === "email" ? "email" : f.type === "url" ? "url" : "text"} disabled={disabled} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />;
  }
}
